<script setup lang="ts">
import { api } from '@/api';
import { type RoleDto } from '@/api/generator';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import type { ContextMenuAction } from '@/components/ContextMenu/types';
import {
  getDefaultContextMenuActions,
  globalContextMenuHeaderItemDefinition,
} from '@/components/ContextMenu/utils';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { alphabeticalStringCompare, localizedEnumCompare } from '@/utils/compareAndSort';
import { translateEntityProperty } from '@/utils/helper.ts';
import { buildOdataQuery } from '@/utils/odata.ts';
import type { I18nKey } from '@/utils/typings.ts';
import { useLoading } from '@/utils/useLoading';
import type { RolesOverviewFilterObject } from '@/views/roles/types.ts';
import { useTranslation } from 'i18next-vue';
import { ref, watchEffect, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import RolesOverviewFilter from './components/RolesOverviewFilter.vue';
import { getTranslatedRoleName, getTranslatedRoleDescritption } from './utils.ts';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange.ts';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';

const { t } = useTranslation();
const router = useRouter();
const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const headers = [
  {
    title: t('RoleName'),
    key: 'displayName',
    sortRaw(roleA: RoleDto, roleB: RoleDto) {
      const roleAName = translateEntityProperty('name', roleA);
      const roleBName = translateEntityProperty('name', roleB);
      if (!roleAName || !roleBName) {
        return -1;
      }
      return alphabeticalStringCompare(roleAName, roleBName);
    },
  },
  {
    title: t('Description'),
    key: 'description',
    width: '30%',
    sortRaw(roleA: RoleDto, roleB: RoleDto) {
      const roleAName = translateEntityProperty('name', roleA);
      const roleBName = translateEntityProperty('name', roleB);
      if (!roleAName || !roleBName) {
        return -1;
      }
      return alphabeticalStringCompare(roleAName, roleBName);
    },
  },
  {
    title: t('RolesNumberOfAssignedAccounts'),
    key: 'assignedUsersCount',
    width: 150,
    align: 'end',
  },
  {
    title: t('StateOfUse'),
    key: 'state',
    sort: localizedEnumCompare('StateOfUseEnum'),
  },
  {
    title: t('Added'),
    key: 'creationTime',
  },
  globalContextMenuHeaderItemDefinition,
] as const;

const roles = ref<RoleDto[]>();
const filterObject = ref<RolesOverviewFilterObject>({});

const {
  action: loadRoles,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  const { result } = await api.rolesApi.getRoleList(
    buildOdataQuery({
      search: filterObject.value?.search,
      filter: filterObject.value?.state?.length
        ? { state: { in: filterObject.value?.state } }
        : undefined,
    }),
  );
  if (result?.value) {
    roles.value = result.value;
  }
}, true);

watchEffect(loadRoles);

function getContextMenuActions(item: RoleDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);
  return [
    {
      ...defaultContextMenuActions.view,
      to: { name: routenames.roles.details, params: { id: item.id } },
    },
    {
      ...defaultContextMenuActions.edit,
      to: { name: routenames.roles.edit, params: { id: item.id } },
    },
  ];
}

function navigateTo(userId: number, hash?: string) {
  router.push({
    name: routenames.roles.details,
    hash,
    params: { id: userId },
  });
}
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('Role_other')" />
    </template>

    <RolesOverviewFilter v-model="filterObject" />

    <OverviewTable
      ref="table"
      class="mt-2"
      :headers="headers"
      :items="roles"
      itemLabel="Role"
      data-testid="DataTable_Role"
      :loading="loading"
      :sortBy="[{ key: 'displayName', order: 'asc' }]"
      :height="height"
      fixedHeader
      @click:row="
        (_event, data) => {
          navigateTo(data.item.id);
        }
      "
    >
      <template #header.displayName="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('Name') }}
          <span class="font-weight-medium">| {{ $t('ID') }}</span>
        </OverviewTableHeader>
      </template>
      <template #item.displayName="{ item }">
        <IdentifierLabel :identifier="item.iuid" :label="getTranslatedRoleName($t, item)" />
        <CommentCount :value="item" @click.stop="navigateTo(item.id, `#${TAB_PREFIX}comments`)" />
      </template>
      <template #item.description="{ item }">
        <p class="truncated">
          {{ getTranslatedRoleDescritption($t, item) }}
        </p>
      </template>

      <template #item.creationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'created')" />
      </template>

      <template #item.state="{ item }">
        {{ $t(`StateOfUseEnum.${item?.state}` as I18nKey) }}
        <EntityChange
          :entityChange="getEntityChangeEvent(item, 'editing')"
          :showEmptyFieldSign="false"
        />
      </template>

      <template #item.contextMenu="{ item }">
        <ContextMenu
          :actions="getContextMenuActions(item)"
          :closeOnScrollElements="tableScrollElement"
        />
      </template>
    </OverviewTable>
  </BasePage>
</template>
